import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, finalize, interval } from 'rxjs';
import { Chart, registerables } from 'chart.js';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { AnalyticsService, DashboardPayload, AnalyticsPeriod } from '../../services/analytics.service';
import { AuthService } from '../../services/auth.service';
import { SiteBuilderService, Site } from '../../services/site-builder.service';
import { StoreService } from '../../services/store.service';
import { I18nService } from '../../i18n/i18n.service';
import { VdCardComponent } from '../../ui/card/card.component';
import { VdTagComponent } from '../../ui/tag/tag.component';
import { VdEmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { VdButtonComponent } from '../../ui/button/button.component';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';
import { OnboardingHeroComponent } from '../../shared/onboarding-cta/onboarding-hero.component';
import {
  VdSectionComponent,
  VdStatGridComponent,
  VdStat,
  VdDataRowComponent,
  ToastService,
} from '../../ui';

Chart.register(...registerables);

// ─── Types locaux ─────────────────────────────────────────────────────
interface CrossListItem {
  key: string;
  label: string;
  meta: string;
  value: string;
  route: string;
  queryParams?: Record<string, any>;
  badgeTone?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';
}

interface FunnelEntry {
  status: string;
  count: number;
  pct: number;
  routeParams?: Record<string, any>;
}

type PulseTone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

interface PulseEvent {
  key: string;
  icon: string;
  label: string;
  tone: PulseTone;
  action?: () => void;
}

interface Insight {
  key: string;
  icon: string;
  tone: PulseTone;
  title: string;
  body: string;
  cta: string;
  action: () => void;
}

interface ChannelTile {
  key: string;
  label: string;
  icon: string;
  tone: PulseTone;
  unread: number;
  connected: boolean;
  comingSoon?: boolean;
}

// ─── Constantes ───────────────────────────────────────────────────────
// Les `labelKey` sont des clés Transloco. Le template applique le pipe
// `transloco` pour rendre la valeur traduite — ce qui rend la liste
// réactive au changement de langue sans rebuild ni signal supplémentaire.
const PERIOD_OPTIONS: { key: AnalyticsPeriod; labelKey: string }[] = [
  { key: 'today',   labelKey: 'accueil.period.today' },
  { key: 'week',    labelKey: 'accueil.period.week' },
  { key: 'month',   labelKey: 'accueil.period.month' },
  { key: 'quarter', labelKey: 'accueil.period.quarter' },
  { key: 'year',    labelKey: 'accueil.period.year' },
];

// Les marques (Messenger, Instagram, WhatsApp, TikTok, Email) sont conservées
// telles quelles : ce sont des noms propres, pas des libellés à traduire.
// Seul `Marketplace` (générique) a une clé i18n dédiée.
const CHANNEL_LABELS: Record<string, { label: string; icon: string; tone: 'primary'|'accent'|'success'|'warning'|'danger'|'info'|'neutral' }> = {
  fb:        { label: 'Messenger',  icon: 'forum',        tone: 'primary' },
  ig:        { label: 'Instagram',  icon: 'photo_camera', tone: 'danger'  },
  wa:        { label: 'WhatsApp',   icon: 'chat',         tone: 'success' },
  email:     { label: 'Email',      icon: 'email',        tone: 'info'    },
  tiktok:    { label: 'TikTok',     icon: 'music_note',   tone: 'neutral' },
};

const CHANNEL_ECOSYSTEM: Array<{
  key: string;
  label: string;
  labelKey?: string;
  icon: string;
  tone: PulseTone;
  comingSoon?: boolean;
}> = [
  { key: 'fb',     label: 'Messenger',                                          icon: 'forum',        tone: 'primary' },
  { key: 'ig',     label: 'Instagram',                                          icon: 'photo_camera', tone: 'danger'  },
  { key: 'wa',     label: 'WhatsApp',                                           icon: 'chat',         tone: 'success' },
  { key: 'tiktok', label: 'TikTok',                                             icon: 'music_note',   tone: 'accent',  comingSoon: true },
  { key: 'market', label: 'Marketplace', labelKey: 'accueil.channels.marketplace', icon: 'storefront', tone: 'info'    },
];

@Component({
  selector: 'app-accueil',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    VdCardComponent,
    VdTagComponent,
    VdEmptyStateComponent,
    VdButtonComponent,
    SkeletonComponent,
    VdSectionComponent,
    VdStatGridComponent,
    VdDataRowComponent,
    OnboardingHeroComponent,
  ],
  templateUrl: './accueil.component.html',
  styleUrls: ['./accueil.component.css'],
})
export class AccueilComponent implements OnInit, OnDestroy, AfterViewInit {
  // ─── Period & lifecycle existant ───────────────────────────
  readonly periods = PERIOD_OPTIONS;
  readonly selectedPeriod = signal<AnalyticsPeriod>('year');
  readonly loading = signal<boolean>(true);
  readonly errorMessage = signal<string | null>(null);
  readonly data = signal<DashboardPayload | null>(null);
  readonly previousData = signal<DashboardPayload | null>(null);

  readonly lastUpdate = signal<Date | null>(null);
  readonly liveStatus = signal<'live' | 'stale'>('live');
  readonly compareEnabled = signal<boolean>(false);

  readonly relativeUpdatedTick = signal<number>(0);

  @ViewChild('revenueCanvas') revenueCanvas?: ElementRef<HTMLCanvasElement>;

  private revenueChart?: Chart;
  private refreshSub?: Subscription;
  private staleSub?: Subscription;
  private sitesSub?: Subscription;
  private storesSub?: Subscription;
  private heroPromptInterval?: ReturnType<typeof setInterval>;
  private heroAnimFrame?: number;

  // ─── Nouveaux signals — Hero & contexte utilisateur ────────
  // `heroPrompts` est une rotation de placeholders pour le composer IA.
  // On stocke les CLÉS i18n et le template applique le pipe transloco.
  readonly heroPrompts = [
    'accueil.composer.placeholders.boost',
    'accueil.composer.placeholders.relaunch',
    'accueil.composer.placeholders.channel',
    'accueil.composer.placeholders.fast_site',
    'accueil.composer.placeholders.top_clients',
  ];
  readonly heroPromptIdx = signal<number>(0);
  readonly composerValue = signal<string>('');
  readonly userFirstname = signal<string>('');

  // ─── Ecosystem signals ─────────────────────────────────────
  readonly mySites = signal<Site[]>([]);
  readonly myStores = signal<any[]>([]);

  // ─── Parallax & confetti ───────────────────────────────────
  readonly heroParallax = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  readonly showConfetti = signal<boolean>(false);

  // ─── Count-up animé pour la stat héros ─────────────────────
  readonly heroAnimatedRevenue = signal<number>(0);

  readonly userRoles = signal<string[]>([]);

  // i18n — on injecte via `inject()` pour garder la signature constructor stable.
  // `i18n.currentLocale()` est lu DANS les `computed()` qui produisent des
  // chaînes traduites pour qu'ils se ré-évaluent sur changement de langue.
  private readonly transloco = inject(TranslocoService);
  private readonly i18n = inject(I18nService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private analytics: AnalyticsService,
    private auth: AuthService,
    private siteBuilder: SiteBuilderService,
    private storeService: StoreService,
    private toast: ToastService,
    private router: Router,
    private zone: NgZone,
  ) {}

  /** Sucre interne : traduit une clé Transloco. */
  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params);
  }

  // ─── Rôles ────────────────────────────────────────────────
  isAdmin(): boolean { return this.userRoles().includes('admin'); }
  isVendor(): boolean { return this.userRoles().includes('vendor'); }
  isEmployee(): boolean { return this.userRoles().includes('employee'); }

  // ─── Greeting / heure du jour (computed) ──────────────────
  // `greetingKey` est consommé par le template via le pipe `transloco`,
  // ce qui rend la salutation réactive au changement de langue. La méthode
  // `greetingText()` est conservée (rétro-compat) et renvoie la valeur
  // déjà traduite — utile si un autre code la lit en TS.
  readonly greetingKey = computed<string>(() => {
    void this.relativeUpdatedTick();
    const h = new Date().getHours();
    if (h < 6)  return 'accueil.greeting.night';
    if (h < 12) return 'accueil.greeting.morning';
    if (h < 18) return 'accueil.greeting.afternoon';
    return 'accueil.greeting.evening';
  });
  readonly greetingText = computed<string>(() => {
    this.i18n.currentLocale();
    return this.t(this.greetingKey());
  });
  /** Conservé pour rétro-compat template existant. */
  greeting(): string { return this.greetingText(); }

  readonly timeOfDayIcon = computed<string>(() => {
    void this.relativeUpdatedTick();
    const h = new Date().getHours();
    if (h < 6)  return 'nights_stay';
    if (h < 12) return 'wb_sunny';
    if (h < 18) return 'light_mode';
    return 'nights_stay';
  });

  // ─── Delta synthétique (utilise previousData OU split intra-série) ─
  readonly compareDeltas = computed<{ revenue: number; orders: number } | null>(() => {
    const curr = this.data();
    if (!curr) return null;
    const prev = this.previousData();
    if (prev) {
      const cr = curr.revenue?.revenue || 0;
      const pr = prev.revenue?.revenue || 0;
      const co = curr.revenue?.orders_count || 0;
      const po = prev.revenue?.orders_count || 0;
      return {
        revenue: pr > 0 ? ((cr - pr) / pr) * 100 : (cr > 0 ? 100 : 0),
        orders:  po > 0 ? ((co - po) / po) * 100 : (co > 0 ? 100 : 0),
      };
    }
    const series = curr.revenue_series || [];
    if (series.length < 4) return null;
    const mid = Math.floor(series.length / 2);
    const prevHalf = series.slice(0, mid);
    const currHalf = series.slice(mid);
    const sum = (arr: any[], k: string) => arr.reduce((a, b) => a + (Number(b[k]) || 0), 0);
    const pr = sum(prevHalf, 'revenue');
    const cr = sum(currHalf, 'revenue');
    const po = sum(prevHalf, 'orders_count');
    const co = sum(currHalf, 'orders_count');
    return {
      revenue: pr > 0 ? ((cr - pr) / pr) * 100 : 0,
      orders:  po > 0 ? ((co - po) / po) * 100 : 0,
    };
  });

  // ─── Météo business ───────────────────────────────────────
  // Approche en 2 computeds : `weatherKey` + `weatherParams`. Le template
  // applique `'... ' | transloco: weatherParams()` ce qui permet :
  //   - réactivité au changement de langue (pipe transloco)
  //   - réactivité au changement de delta (signal computed)
  // `businessWeather()` est gardé en rétro-compat (renvoie déjà traduit).
  readonly weatherKey = computed<string>(() => {
    const d = this.compareDeltas();
    if (!d) return 'accueil.weather.idle';
    const r = d.revenue;
    if (r > 20)  return 'accueil.weather.excellent';
    if (r > 5)   return 'accueil.weather.good';
    if (r > 0)   return 'accueil.weather.up';
    if (Math.abs(r) < 1) return 'accueil.weather.stable';
    return 'accueil.weather.optimize';
  });
  readonly weatherParams = computed<Record<string, string>>(() => {
    const d = this.compareDeltas();
    if (!d) return {} as Record<string, string>;
    const r = d.revenue;
    // `up` (entre 0 et 5%) garde 1 décimale ; les autres arrondissent.
    return { delta: r.toFixed(r > 0 && r <= 5 ? 1 : 0) };
  });
  readonly businessWeather = computed<string>(() => {
    this.i18n.currentLocale();
    return this.t(this.weatherKey(), this.weatherParams());
  });

  // ─── Sparkline 12 derniers points (SVG) ───────────────────
  readonly heroSparkline = computed<number[]>(() => {
    const series = this.data()?.revenue_series || [];
    return series.slice(-12).map((p) => Number(p?.revenue) || 0);
  });

  // ─── CA du jour / delta vs hier ───────────────────────────
  readonly todayRevenue = computed<number>(() => {
    const s = this.data()?.revenue_series || [];
    if (!s.length) return this.data()?.revenue?.revenue || 0;
    return Number(s[s.length - 1]?.revenue) || 0;
  });
  readonly yesterdayDelta = computed<number | null>(() => {
    const s = this.data()?.revenue_series || [];
    if (s.length < 2) return null;
    const today = Number(s[s.length - 1]?.revenue) || 0;
    const yest  = Number(s[s.length - 2]?.revenue) || 0;
    if (yest === 0) return today > 0 ? 100 : 0;
    return ((today - yest) / yest) * 100;
  });

  // ─── Pulse timeline (5-7 mini-cards d'événements) ─────────
  // `label` est traduit DANS le computed (et non via le pipe), parce que le
  // template fait `{{ p.label }}` directement. Pour la réactivité au
  // changement de langue, on lit `i18n.currentLocale()` comme dépendance
  // explicite : le computed se ré-évaluera quand la locale change.
  readonly pulseEvents = computed<PulseEvent[]>(() => {
    this.i18n.currentLocale();
    const d = this.data();
    if (!d) return [];
    const events: PulseEvent[] = [];

    const rev = d.revenue;
    if (rev?.orders_count > 0) {
      const isOne = rev.orders_count === 1;
      events.push({
        key: 'orders-recent',
        icon: 'receipt_long',
        label: this.t(
          isOne ? 'accueil.pulse.orders_one' : 'accueil.pulse.orders_many',
          { count: rev.orders_count },
        ),
        tone: 'primary',
        action: () => this.router.navigate(['/commande']),
      });
    }

    const inbox = d.inbox_volume || [];
    const totalUnread = inbox.reduce((acc, c) => acc + (c.unread || 0), 0);
    if (totalUnread > 0) {
      const top = [...inbox].sort((a,b) => b.unread - a.unread)[0];
      const meta = CHANNEL_LABELS[top.channel] || { label: top.channel, icon: 'chat', tone: 'neutral' };
      const isOne = top.unread === 1;
      events.push({
        key: 'inbox-unread',
        icon: 'mark_email_unread',
        label: this.t(
          isOne ? 'accueil.pulse.unread_one' : 'accueil.pulse.unread_many',
          { count: top.unread, channel: meta.label },
        ),
        tone: 'danger',
        action: () => this.router.navigate(['/messages'], { queryParams: { channel: top.channel } }),
      });
    }

    const sites = this.mySites();
    const liveSite = sites.find((s) => s.status === 'live');
    if (liveSite) {
      events.push({
        key: `site-${liveSite.id}`,
        icon: 'language',
        label: this.t('accueil.pulse.site_live', { name: liveSite.name }),
        tone: 'success',
        action: () => this.router.navigate(['/sites', liveSite.id]),
      });
    }

    const yd = this.yesterdayDelta();
    if (yd !== null && Math.abs(yd) >= 1) {
      events.push({
        key: 'delta-yesterday',
        icon: yd > 0 ? 'trending_up' : 'trending_down',
        label: this.t('accueil.pulse.delta_yesterday', {
          sign: yd > 0 ? '+' : '',
          value: yd.toFixed(0),
        }),
        tone: yd > 0 ? 'success' : 'warning',
      });
    }

    const topProd = d.top_products?.[0];
    if (topProd) {
      events.push({
        key: `prod-${topProd.product_id}`,
        icon: 'star',
        label: this.t('accueil.pulse.top_product', { name: topProd.name }),
        tone: 'accent',
        action: () => this.router.navigate(['/produits'], { queryParams: { id: topProd.product_id } }),
      });
    }

    const enCours = Number(d.funnel?.['en_cours'] || d.funnel?.['in_progress'] || 0);
    if (enCours > 0) {
      const isOne = enCours === 1;
      events.push({
        key: 'shipping',
        icon: 'local_shipping',
        label: this.t(
          isOne ? 'accueil.pulse.shipping_one' : 'accueil.pulse.shipping_many',
          { count: enCours },
        ),
        tone: 'warning',
        action: () => this.router.navigate(['/commande'], { queryParams: { status: 'en_cours' } }),
      });
    }

    return events.slice(0, 6);
  });

  // ─── Canaux sociaux (ecosystem) ───────────────────────────
  // Si une `labelKey` i18n est définie (ex: `Marketplace`), on traduit ; sinon
  // on garde la marque originale (Messenger, Instagram, WhatsApp).
  readonly channelTiles = computed<ChannelTile[]>(() => {
    this.i18n.currentLocale();
    const inbox = this.data()?.inbox_volume || [];
    const byKey = new Map(inbox.map((c) => [c.channel, c]));
    return CHANNEL_ECOSYSTEM.map((c) => {
      const row = byKey.get(c.key);
      return {
        key: c.key,
        label: c.labelKey ? this.t(c.labelKey) : c.label,
        icon: c.icon,
        tone: c.tone,
        unread: row?.unread || 0,
        connected: !!row,
        comingSoon: c.comingSoon,
      };
    });
  });

  // ─── Insights IA ──────────────────────────────────────────
  // Le computed lit `i18n.currentLocale()` pour se ré-évaluer sur changement
  // de langue : les `title`/`body`/`cta` sont déjà traduits dans `buildInsights`.
  readonly insights = computed<Insight[]>(() => {
    this.i18n.currentLocale();
    return this.buildInsights();
  });

  private funnelAbandonedRate(): number {
    const f = this.data()?.funnel || {};
    const ab = Number(f['abandonne'] || f['abandoned'] || f['panier'] || f['cart'] || 0);
    const total = Object.values(f).reduce((a, b) => a + (Number(b) || 0), 0);
    if (!total) return 0;
    return ab / total;
  }

  private buildInsights(): Insight[] {
    const d = this.data();
    if (!d) return [];
    const list: Insight[] = [];

    if (this.mySites().length === 0) {
      list.push({
        key: 'launch-site',
        icon: 'rocket_launch',
        tone: 'primary',
        title: this.t('accueil.insights.launch_site_title'),
        body:  this.t('accueil.insights.launch_site_body'),
        cta:   this.t('accueil.insights.launch_site_cta'),
        action: () => this.router.navigate(['/onboarding-site']),
      });
    }

    const abRate = this.funnelAbandonedRate();
    if (abRate > 0.3) {
      list.push({
        key: 'abandoned-carts',
        icon: 'shopping_cart',
        tone: 'warning',
        title: this.t('accueil.insights.abandoned_carts_title', { rate: Math.round(abRate * 100) }),
        body:  this.t('accueil.insights.abandoned_carts_body'),
        cta:   this.t('accueil.insights.abandoned_carts_cta'),
        action: () => this.toast.info(
          this.t('accueil.toast.reminders_phase2'),
          this.t('accueil.toast.insights'),
        ),
      });
    }

    const topProd = d.top_products?.[0];
    if (topProd) {
      list.push({
        key: `boost-${topProd.product_id}`,
        icon: 'star',
        tone: 'success',
        title: this.t('accueil.insights.boost_product_title', { name: topProd.name }),
        body:  this.t('accueil.insights.boost_product_body'),
        cta:   this.t('accueil.insights.boost_product_cta'),
        action: () => {
          const sid = this.mySites()[0]?.id;
          if (sid) {
            this.router.navigate(['/sites', sid, 'studio'], {
              // Le prompt q= est destiné à l'assistant IA (Studio) et reste
              // dans la langue d'origine de l'utilisateur via la traduction.
              queryParams: { q: this.t('accueil.insights.boost_product_prompt', { name: topProd.name }) },
            });
          } else {
            this.router.navigate(['/sites']);
          }
        },
      });
    }

    const topChan = [...(d.inbox_volume || [])].sort((a, b) => b.unread - a.unread)[0];
    if (topChan && topChan.unread > 0) {
      const meta = CHANNEL_LABELS[topChan.channel] || { label: topChan.channel };
      const isOne = topChan.unread === 1;
      list.push({
        key: `chan-${topChan.channel}`,
        icon: 'forum',
        tone: 'info',
        title: this.t('accueil.insights.channel_pending_title', { channel: meta.label }),
        body: this.t(
          isOne ? 'accueil.insights.channel_pending_body_one' : 'accueil.insights.channel_pending_body_many',
          { count: topChan.unread },
        ),
        cta: this.t('accueil.insights.channel_pending_cta'),
        action: () => this.router.navigate(['/messages'], { queryParams: { channel: topChan.channel } }),
      });
    }

    const customDomainPending = this.mySites().find((s: any) => s.has_pending_domain);
    if (customDomainPending) {
      list.push({
        key: `dom-${customDomainPending.id}`,
        icon: 'public',
        tone: 'accent',
        title: this.t('accueil.insights.domain_pending_title'),
        body:  this.t('accueil.insights.domain_pending_body', { name: customDomainPending.name }),
        cta:   this.t('accueil.insights.domain_pending_cta'),
        action: () => this.router.navigate(['/sites', customDomainPending.id, 'domains']),
      });
    }

    return list.slice(0, 4);
  }

  triggerInsight(i: Insight): void {
    try { i.action?.(); } catch {}
  }

  // ─── Lifecycle ────────────────────────────────────────────
  ngOnInit(): void {
    try {
      const roles = this.auth.getRolesFromToken?.() || [];
      this.userRoles.set(roles);
    } catch {}

    // Identité affichée
    try {
      const full = (this.auth as any).getFullnameFromToken?.()
                 || (this.auth as any).getUsernameFromToken?.()
                 || '';
      const first = String(full).split(' ')[0] || '';
      this.userFirstname.set(first);
    } catch {}

    // Sites du vendor (top 3 affichés, +1 pour CTA aware)
    try {
      this.sitesSub = this.siteBuilder.listSites?.().subscribe({
        next: (sites) => this.mySites.set((sites || []).slice(0, 5)),
        error: () => this.mySites.set([]),
      });
    } catch { this.mySites.set([]); }

    // Boutiques (top 3)
    try {
      this.storesSub = this.storeService.getAllStores?.().subscribe({
        next: (stores) => this.myStores.set((stores || []).slice(0, 3)),
        error: () => this.myStores.set([]),
      });
    } catch { this.myStores.set([]); }

    // Rotation placeholder composer
    if (isPlatformBrowser(this.platformId)) {
      this.heroPromptInterval = setInterval(() => {
        this.heroPromptIdx.update((i) => (i + 1) % this.heroPrompts.length);
      }, 4000);
    }

    this.fetch();
    this.refreshSub = interval(60_000).subscribe(() => this.fetch(true));
    this.staleSub = interval(5_000).subscribe(() => {
      this.relativeUpdatedTick.update((v) => v + 1);
      const updated = this.lastUpdate();
      if (updated) {
        const ageSec = (Date.now() - updated.getTime()) / 1000;
        this.liveStatus.set(ageSec > 120 ? 'stale' : 'live');
      }
    });
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.staleSub?.unsubscribe();
    this.sitesSub?.unsubscribe();
    this.storesSub?.unsubscribe();
    if (this.heroPromptInterval) clearInterval(this.heroPromptInterval);
    if (this.heroAnimFrame && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.heroAnimFrame);
    }
    this.revenueChart?.destroy();
  }

  // ─── Parallax souris ──────────────────────────────────────
  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    const x = ((e.clientX / w) - 0.5) * 8;
    const y = ((e.clientY / h) - 0.5) * 8;
    this.heroParallax.set({ x, y });
  }

  // ─── Composer héros ───────────────────────────────────────
  submitHeroComposer(): void {
    const q = this.composerValue().trim();
    if (!q) return;
    const sid = this.mySites()[0]?.id;
    if (sid) {
      this.router.navigate(['/sites', sid, 'studio'], { queryParams: { q } });
    } else {
      this.router.navigate(['/sites']);
    }
    this.composerValue.set('');
  }

  onComposerKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submitHeroComposer();
    }
  }

  // ─── Pulse click ──────────────────────────────────────────
  triggerPulse(ev: PulseEvent): void {
    if (ev.action) try { ev.action(); } catch {}
  }

  // ─── Hero quick CTA ──────────────────────────────────────
  goToFirstSite(): void {
    const s = this.mySites()[0];
    if (s) this.router.navigate(['/sites', s.id]);
    else this.router.navigate(['/sites']);
  }
  goToNewSite(): void { this.router.navigate(['/onboarding-site']); }
  goToInbox(): void { this.router.navigate(['/messages']); }

  inboxUnreadCount(): number {
    return (this.data()?.inbox_volume || []).reduce((acc, c) => acc + (c.unread || 0), 0);
  }

  // ─── Period & refresh existants ───────────────────────────
  setPeriod(p: AnalyticsPeriod): void {
    if (this.selectedPeriod() === p) return;
    this.selectedPeriod.set(p);
    this.previousData.set(null);
    this.compareEnabled.set(false);
    this.fetch();
  }

  refresh(): void { this.fetch(); }

  toggleCompare(): void {
    this.compareEnabled.update((v) => !v);
    if (this.compareEnabled() && !this.previousData()) {
      this.fetchPrevious();
    } else {
      setTimeout(() => this.renderRevenueChart(), 60);
    }
  }

  // ─── Top lists existantes ─────────────────────────────────
  topProducts() { return this.data()?.top_products || []; }
  topCustomers() { return this.data()?.top_customers || []; }

  // ⚠️ computed MÉMOÏSÉ et NON une méthode.
  //
  // CAUSE RACINE du gel de /Accueil : en méthode, `funnelEntries()` renvoyait
  // un NOUVEAU tableau de NOUVEAUX objets à CHAQUE lecture. Le *ngFor associé
  // (trackBy = identité par défaut) voyait donc une collection entièrement
  // « nouvelle » à chaque passe de détection de changement → destruction +
  // recréation de toutes les lignes → la vue se re-marque sale → la boucle
  // `synchronize` de la CD se relance → ré-évaluation → nouveaux objets → …
  // boucle infinie (NG0100 en dev, gel silencieux du thread en prod ; seule
  // page concernée car seule à utiliser ce motif).
  // Un computed renvoie la MÊME référence (et les mêmes objets) tant que
  // `data()` ne change pas → *ngFor stable → CD stabilisée.
  readonly funnelEntries = computed<FunnelEntry[]>(() => {
    const f = this.data()?.funnel || {};
    const arr = Object.entries(f).map(([status, count]) => ({ status, count: Number(count) || 0 }));
    const total = arr.reduce((acc, e) => acc + e.count, 0) || 1;
    return arr
      .sort((a, b) => b.count - a.count)
      .map((e) => ({
        status: e.status,
        count: e.count,
        pct: Math.round((e.count / total) * 100),
        routeParams: { status: e.status },
      }));
  });

  formatMoney(value: number): string {
    if (value == null || isNaN(value)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  /** Retrocompat — utilisé jadis dans template. Préfère `userFirstname()`. */
  userFirstName(): string { return this.userFirstname(); }

  // ─── KPI tiles INTERACTIFS ──────────────────────────────
  // `vd-stat-grid` rend `s.label`, `s.delta`, `s.hint` SANS pipe transloco
  // (cf. stat-grid.component.ts) — donc la traduction se fait ICI dans le
  // computed. Lecture de `i18n.currentLocale()` pour la réactivité.
  readonly kpiStats = computed<VdStat[]>(() => {
    this.i18n.currentLocale();
    const d = this.data();
    if (!d) return [];
    const rev = d.revenue;
    const totalInbox = (d.inbox_volume || []).reduce((acc, c) => acc + c.conversations, 0);
    const unreadInbox = (d.inbox_volume || []).reduce((acc, c) => acc + c.unread, 0);
    const enCoursFunnel = Number(d.funnel?.['en_cours'] || d.funnel?.['in_progress'] || 0);

    const prev = this.previousData();
    const deltaPct = (current: number, previous: number) => {
      if (!previous) return undefined;
      const pct = ((current - previous) / previous) * 100;
      const sign = pct >= 0 ? '+' : '';
      return this.t('accueil.kpi.delta_vs_previous', { sign, value: pct.toFixed(0) });
    };

    return [
      {
        key: 'revenue',
        label: this.t('accueil.kpi.revenue_label'),
        icon: 'payments',
        value: this.formatMoney(rev.revenue),
        tone: 'primary',
        delta: deltaPct(rev.revenue, prev?.revenue?.revenue || 0)
              || this.t('accueil.kpi.orders_short', { count: rev.orders_count }),
        hint: this.t('accueil.kpi.revenue_hint'),
      },
      {
        key: 'orders',
        label: this.t('accueil.kpi.orders_label'),
        icon: 'inventory_2',
        value: String(rev.orders_count),
        tone: rev.cancelled_count > 0 ? 'warning' : 'success',
        delta: deltaPct(rev.orders_count, prev?.revenue?.orders_count || 0)
              || (rev.cancelled_count > 0
                ? this.t('accueil.kpi.orders_cancelled', { count: rev.cancelled_count })
                : this.t('accueil.kpi.orders_no_cancellation')),
        hint: this.t('accueil.kpi.orders_hint'),
      },
      {
        key: 'aov',
        label: this.t('accueil.kpi.aov_label'),
        icon: 'shopping_bag',
        value: this.formatMoney(rev.aov),
        tone: 'info',
        delta: deltaPct(rev.aov, prev?.revenue?.aov || 0)
              || this.t('accueil.kpi.aov_clients', { count: rev.unique_customers }),
        hint: this.t('accueil.kpi.aov_hint'),
      },
      {
        key: 'conversion',
        label: this.t('accueil.kpi.conversion_label'),
        icon: 'trending_up',
        value: this.conversionRate(),
        tone: 'neutral',
        delta: deltaPct(this.conversionRateNum(d), this.conversionRateNum(prev))
              || this.t('accueil.kpi.conversion_delta'),
        hint: this.t('accueil.kpi.conversion_hint'),
      },
      {
        key: 'inbox',
        label: this.t('accueil.kpi.inbox_label'),
        icon: 'mark_email_unread',
        value: String(unreadInbox),
        tone: unreadInbox > 0 ? 'danger' : 'success',
        delta: deltaPct(unreadInbox, this.previousUnread() || 0)
              || this.t('accueil.kpi.inbox_total', { count: totalInbox }),
        hint: this.t('accueil.kpi.inbox_hint'),
      },
      {
        key: 'deliveries',
        label: this.t('accueil.kpi.deliveries_label'),
        icon: 'local_shipping',
        value: String(enCoursFunnel),
        tone: 'warning',
        delta: deltaPct(enCoursFunnel, this.deliveriesCount(prev))
              || this.t('accueil.kpi.deliveries_delta'),
        hint: this.t('accueil.kpi.deliveries_hint'),
      },
    ];
  });

  onKpiClick(key: string): void {
    switch (key) {
      case 'revenue':
        this.router.navigate(['/commande'], { queryParams: { period: this.selectedPeriod() } });
        break;
      case 'orders':
        this.router.navigate(['/commande']);
        break;
      case 'aov':
        this.router.navigate(['/commande'], { queryParams: { sort: 'high' } });
        break;
      case 'inbox':
        this.router.navigate(['/messages'], { queryParams: { status: 'open' } });
        break;
      case 'deliveries':
        this.router.navigate(['/commande'], { queryParams: { status: 'en_cours' } });
        break;
      case 'conversion':
        this.scrollToFunnel();
        break;
    }
  }

  // ─── Cross views ────────────────────────────────────────
  // Mêmes contraintes que `kpiStats` : `vd-data-row` rend `[label]`/`[value]`
  // sans pipe transloco, donc on traduit ici. `i18n.currentLocale()` est lu
  // explicitement comme dépendance pour la réactivité.
  readonly topCustomersCross = computed<CrossListItem[]>(() => {
    this.i18n.currentLocale();
    return (this.data()?.top_customers || []).slice(0, 5).map((c) => ({
      key: String(c.customer_id),
      label: c.name || this.t('accueil.cross.customer_unknown'),
      meta: this.t(
        c.orders_count === 1 ? 'accueil.cross.orders_one' : 'accueil.cross.orders_many',
        { count: c.orders_count },
      ),
      value: this.formatMoney(c.revenue),
      route: '/clients',
      queryParams: { id: c.customer_id },
      badgeTone: 'primary',
    }));
  });

  readonly topProductsCross = computed<CrossListItem[]>(() => {
    this.i18n.currentLocale();
    return (this.data()?.top_products || []).slice(0, 5).map((p) => ({
      key: String(p.product_id),
      label: p.name,
      meta: this.t('accueil.cross.units_sold', { count: p.units_sold }),
      value: this.formatMoney(p.revenue),
      route: '/produits',
      queryParams: { id: p.product_id },
      badgeTone: 'accent',
    }));
  });

  readonly topChannelsCross = computed<CrossListItem[]>(() => {
    this.i18n.currentLocale();
    return (this.data()?.inbox_volume || [])
      .slice()
      .sort((a, b) => b.conversations - a.conversations)
      .slice(0, 5)
      .map((c) => {
        const meta = CHANNEL_LABELS[c.channel] || { label: c.channel, icon: 'chat', tone: 'neutral' as const };
        return {
          key: c.channel,
          label: meta.label,
          meta: this.t(
            c.unread === 1 ? 'accueil.cross.unread_one' : 'accueil.cross.unread_many',
            { count: c.unread },
          ),
          value: this.t('accueil.cross.conv_count', { count: c.conversations }),
          route: '/messages',
          queryParams: { channel: c.channel },
          badgeTone: meta.tone,
        };
      });
  });

  navigateCross(item: CrossListItem): void {
    this.router.navigate([item.route], { queryParams: item.queryParams || {} });
  }

  // ─── Funnel drill-down ──────────────────────────────────
  drillFunnel(entry: FunnelEntry): void {
    this.router.navigate(['/commande'], { queryParams: { status: entry.status } });
  }

  // ─── Quick actions (contextuelles par rôle) ─────────────
  // `label` est traduit ici (et non via le pipe) car le template fait
  // `{{ a.label }}` directement. `i18n.currentLocale()` => réactivité langue.
  readonly quickActions = computed<{ key: string; icon: string; label: string; route: string; queryParams?: Record<string, any>; tone: 'primary'|'accent'|'success'|'warning'|'danger' }[]>(() => {
    this.i18n.currentLocale();
    const actions: { key: string; icon: string; label: string; route: string; queryParams?: Record<string, any>; tone: 'primary'|'accent'|'success'|'warning'|'danger' }[] = [];
    if (this.isAdmin() || this.isVendor()) {
      actions.push({ key: 'order-new',   icon: 'add_shopping_cart', label: this.t('accueil.quick.order_new'),    route: '/commande', queryParams: { open: 'new' }, tone: 'primary' });
      actions.push({ key: 'product-new', icon: 'inventory_2',       label: this.t('accueil.quick.product_new'),  route: '/produits', queryParams: { open: 'new' }, tone: 'accent'  });
      actions.push({ key: 'client-new',  icon: 'person_add',        label: this.t('accueil.quick.client_new'),   route: '/clients',  queryParams: { open: 'new' }, tone: 'success' });
      actions.push({ key: 'site-new',    icon: 'auto_awesome',      label: this.t('accueil.quick.site_new'),     route: '/onboarding-site', tone: 'accent' });
      actions.push({ key: 'messages',    icon: 'forum',             label: this.t('accueil.quick.see_messages'), route: '/messages', queryParams: { status: 'open' }, tone: 'warning' });
    }
    if (this.isEmployee() && !this.isVendor() && !this.isAdmin()) {
      actions.push({ key: 'order-new', icon: 'add_shopping_cart', label: this.t('accueil.quick.order_new'), route: '/commande', queryParams: { open: 'new' }, tone: 'primary' });
      actions.push({ key: 'my-tasks',  icon: 'task_alt',          label: this.t('accueil.quick.my_tasks'),  route: '/commande', queryParams: { assignee: 'me' }, tone: 'accent' });
    }
    return actions;
  });

  triggerQuickAction(a: { route: string; queryParams?: Record<string, any> }, event?: MouseEvent): void {
    if (event?.currentTarget instanceof HTMLElement) {
      const el = event.currentTarget;
      el.classList.remove('is-rippling');
      void el.offsetWidth;
      el.classList.add('is-rippling');
      setTimeout(() => el.classList.remove('is-rippling'), 500);
    }
    this.router.navigate([a.route], { queryParams: a.queryParams || {} });
  }

  goTo(route: string): void { this.router.navigateByUrl(route); }

  // ─── Helpers existants ────────────────────────────────────
  /** Taux de conversion NUMÉRIQUE (% commandes payées / total) — sert au delta. */
  private conversionRateNum(d: DashboardPayload | null): number {
    if (!d) return 0;
    const f = d.funnel || {};
    const ordersTotal = Object.values(f).reduce((acc, n) => acc + (Number(n) || 0), 0);
    if (!ordersTotal) return 0;
    const paid = Number(f['payee'] || f['paid'] || f['paye'] || 0);
    return (paid / ordersTotal) * 100;
  }

  /** Nb de livraisons « en cours » d'un payload (pour delta période-sur-période). */
  private deliveriesCount(d: DashboardPayload | null): number {
    if (!d) return 0;
    return Number(d.funnel?.['en_cours'] || d.funnel?.['in_progress'] || 0);
  }

  conversionRate(): string {
    return `${Math.round(this.conversionRateNum(this.data()))}%`;
  }

  previousUnread(): number {
    const prev = this.previousData();
    if (!prev) return 0;
    return (prev.inbox_volume || []).reduce((acc, c) => acc + (c.unread || 0), 0);
  }

  // Libellé « Mis à jour il y a … » — computed MÉMOÏSÉ (et non une méthode).
  // Une méthode était réévaluée à CHAQUE passe de détection de changement et
  // lisait Date.now() en direct : en dev-mode la double-passe voyait « 20s »
  // puis « 21s » → NG0100 ExpressionChangedAfterItHasBeenChecked à chaque tick,
  // ce qui (avec l'intervalle 5s) saturait la CD et figeait l'onglet.
  // Un computed ne se recalcule QUE lorsqu'une de ses dépendances signal change
  // (relativeUpdatedTick toutes les 5s, ou lastUpdate) : la valeur est donc
  // stable entre les deux passes d'un même cycle → plus de NG0100.
  readonly relativeUpdated = computed<string>(() => {
    this.relativeUpdatedTick();
    this.i18n.currentLocale();
    const updated = this.lastUpdate();
    if (!updated) return this.t('accueil.relative.never');
    const ageSec = Math.floor((Date.now() - updated.getTime()) / 1000);
    if (ageSec < 5) return this.t('accueil.relative.now');
    if (ageSec < 60) return this.t('accueil.relative.seconds', { count: ageSec });
    const ageMin = Math.floor(ageSec / 60);
    if (ageMin < 60) return this.t('accueil.relative.minutes', { count: ageMin });
    const ageHr = Math.floor(ageMin / 60);
    return this.t('accueil.relative.hours', { count: ageHr });
  });

  scrollToFunnel(): void {
    if (typeof document === 'undefined') return;
    const el = document.querySelector('.funnel-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ─── Sites helpers (template) ─────────────────────────────
  sitePreviewUrl(siteId: string): string {
    // Iframe preview interne — chemin standard utilisé dans /sites
    return `/preview/${siteId}`;
  }
  siteStatusTone(status?: string): 'success' | 'warning' | 'neutral' | 'danger' {
    if (status === 'live') return 'success';
    if (status === 'draft') return 'warning';
    if (status === 'suspended') return 'danger';
    return 'neutral';
  }
  openSite(s: Site): void { this.router.navigate(['/sites', s.id]); }
  openSiteStudio(s: Site): void { this.router.navigate(['/sites', s.id, 'studio']); }
  goToStores(): void { this.router.navigateByUrl('/store'); }
  goToSites(): void { this.router.navigateByUrl('/sites'); }
  goToMessages(): void { this.router.navigateByUrl('/messages'); }
  openChannel(c: ChannelTile): void {
    if (c.comingSoon) {
      this.router.navigate(['/marketing', c.key]);
      return;
    }
    this.router.navigate(['/messages'], { queryParams: { channel: c.key } });
  }

  // ─── SVG sparkline (path d) ───────────────────────────────
  sparklinePath(): string {
    const pts = this.heroSparkline();
    if (!pts.length) return '';
    const max = Math.max(...pts, 1);
    const min = Math.min(...pts, 0);
    const range = (max - min) || 1;
    const w = 220, h = 56;
    const step = pts.length > 1 ? w / (pts.length - 1) : w;
    return pts.map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }
  sparklineAreaPath(): string {
    const pts = this.heroSparkline();
    if (!pts.length) return '';
    const max = Math.max(...pts, 1);
    const min = Math.min(...pts, 0);
    const range = (max - min) || 1;
    const w = 220, h = 56;
    const step = pts.length > 1 ? w / (pts.length - 1) : w;
    const linePath = pts.map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return `${linePath} L ${w},${h} L 0,${h} Z`;
  }

  // ─── Particules confetti ──────────────────────────────────
  readonly confettiParticles = Array.from({ length: 6 }, (_, i) => i);

  private maybeFireConfetti(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const d = this.compareDeltas();
    if (d && d.revenue > 20) {
      this.showConfetti.set(true);
      setTimeout(() => this.showConfetti.set(false), 3500);
    }
  }

  // ─── Count-up sur le CA héros ────────────────────────────
  private animateHeroRevenue(target: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.heroAnimatedRevenue.set(target);
      return;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.heroAnimatedRevenue.set(target);
      return;
    }
    const start = this.heroAnimatedRevenue();
    if (start === target) return;
    const duration = 900;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = start + (target - start) * eased;
      this.heroAnimatedRevenue.set(val);
      if (p < 1) this.heroAnimFrame = requestAnimationFrame(tick);
    };
    if (this.heroAnimFrame) cancelAnimationFrame(this.heroAnimFrame);
    this.heroAnimFrame = requestAnimationFrame(tick);
  }

  // ─── Fetching ─────────────────────────────────────────
  private fetch(silent = false): void {
    if (!silent) this.loading.set(true);
    this.errorMessage.set(null);
    this.analytics
      .dashboard(this.selectedPeriod())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (payload) => {
          this.data.set(payload);
          this.lastUpdate.set(new Date());
          this.liveStatus.set('live');
          this.animateHeroRevenue(this.todayRevenue());
          this.maybeFireConfetti();
          setTimeout(() => this.renderRevenueChart(), 60);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.detail || err?.message || this.t('accueil.error.loading'));
        },
      });
  }

  private fetchPrevious(): void {
    const prevPeriod = this.previousPeriodMapping(this.selectedPeriod());
    this.analytics.dashboard(prevPeriod).subscribe({
      next: (payload) => {
        this.previousData.set(payload);
        setTimeout(() => this.renderRevenueChart(), 60);
      },
      error: () => {},
    });
  }

  private previousPeriodMapping(p: AnalyticsPeriod): AnalyticsPeriod {
    if (p === 'today')   return 'week';
    if (p === 'week')    return 'month';
    if (p === 'month')   return 'quarter';
    if (p === 'quarter') return 'year';
    return 'year';
  }

  private renderRevenueChart(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const payload = this.data();
    if (!payload) return;
    if (!this.revenueCanvas?.nativeElement) return;

    const ctx = this.revenueCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    const labels = (payload.revenue_series || []).map((p) => p.day);
    const revenues = (payload.revenue_series || []).map((p) => p.revenue);
    const orders = (payload.revenue_series || []).map((p) => p.orders_count);

    const gradient = ctx.createLinearGradient(0, 0, 0, 260);
    gradient.addColorStop(0, 'rgba(99, 102, 241, 0.55)');
    gradient.addColorStop(0.5, 'rgba(139, 92, 246, 0.18)');
    gradient.addColorStop(1, 'rgba(99, 102, 241, 0.02)');

    const datasets: any[] = [
      {
        label: this.t('accueil.revenue.dataset_revenue'),
        data: revenues,
        borderColor: '#6366f1',
        backgroundColor: gradient,
        borderWidth: 2.5,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#6366f1',
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 3,
        pointHoverRadius: 7,
        orderCounts: orders,
      },
    ];

    if (this.compareEnabled() && this.previousData()) {
      const prev = this.previousData()!;
      const prevRev = (prev.revenue_series || []).map((p) => p.revenue);
      datasets.push({
        label: this.t('accueil.revenue.compare_legend'),
        data: prevRev,
        borderColor: '#94a3b8',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [6, 4],
        fill: false,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
      });
    }

    // Chart.js créé HORS de la zone Angular : son ResizeObserver interne
    // (responsive:true), patché par zone.js, déclencherait sinon un tick de CD
    // à chaque redimensionnement → boucle observer→tick→layout→observer. En
    // dehors de la zone, ces callbacks n'amorcent plus la détection de
    // changement. On re-rentre explicitement dans la zone pour la navigation.
    this.zone.runOutsideAngular(() => {
    this.revenueChart?.destroy();
    this.revenueChart = new Chart(ctx, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        onClick: (_event, elements) => {
          if (!elements?.length) return;
          const idx = elements[0].index;
          const day = labels[idx];
          if (!day) return;
          this.zone.run(() => this.router.navigate(['/commande'], {
            queryParams: { period: this.selectedPeriod(), day },
          }));
        },
        onHover: (event, elements) => {
          const target = event.native?.target as HTMLElement | undefined;
          if (target) target.style.cursor = elements.length ? 'pointer' : 'default';
        },
        plugins: {
          legend: { display: this.compareEnabled() },
          tooltip: {
            backgroundColor: 'rgba(15, 18, 35, 0.96)',
            padding: 14,
            cornerRadius: 10,
            titleColor: '#fff',
            bodyColor: '#cbd5e1',
            displayColors: false,
            titleFont: { weight: 700, size: 12 },
            bodyFont: { size: 12 },
            callbacks: {
              title: (items) => this.t('accueil.revenue.day', { day: items[0]?.label ?? '' }),
              label: (item) => {
                const ds: any = item.dataset;
                const lines = [
                  this.t('accueil.revenue.tooltip_revenue', { value: this.formatMoney(Number(item.parsed.y)) }),
                ];
                if (ds.orderCounts && ds.orderCounts[item.dataIndex] !== undefined) {
                  lines.push(this.t('accueil.revenue.tooltip_orders', { count: ds.orderCounts[item.dataIndex] }));
                }
                if (item.dataIndex > 0 && ds.data) {
                  const prevVal = Number(ds.data[item.dataIndex - 1]) || 0;
                  const currVal = Number(item.parsed.y) || 0;
                  if (prevVal > 0) {
                    const delta = ((currVal - prevVal) / prevVal) * 100;
                    const sign = delta >= 0 ? '+' : '';
                    lines.push(this.t('accueil.revenue.tooltip_variation', { sign, value: delta.toFixed(0) }));
                  }
                }
                lines.push(this.t('accueil.revenue.tooltip_click_to_open'));
                return lines;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: 'rgba(148, 163, 184, 0.7)', font: { size: 10 } },
          },
          y: {
            grid: { color: 'rgba(148, 163, 184, 0.12)' },
            ticks: {
              color: 'rgba(148, 163, 184, 0.7)',
              font: { size: 10 },
              callback: (val) => this.formatMoney(Number(val)),
            },
          },
        },
      },
    });
    });
  }
}
